import React from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  Box,
  Typography,
  Avatar,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import LockIcon from "@mui/icons-material/LockRounded";
import { getAvatarColor } from "helpers/avatarHelpers";

/**
 * Dialog shown when a record is locked by another user
 * @param {boolean} open - Whether the dialog is visible
 * @param {object} lockedBy - User who has the lock { userId, userName, imageThumbnail }
 * @param {function} onClose - Callback when user clicks close (to exit overlay)
 */
const EditLockDialog = ({ open, lockedBy, onClose }) => {
  const theme = useTheme();

  if (!lockedBy) return null;

  const avatarColor = getAvatarColor(lockedBy.userName || "User");

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: "12px",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          pb: 1,
        }}
      >
        <LockIcon color="warning" />
        <Typography component="span" variant="h6">
          Record Locked
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            py: 2,
          }}
        >
          <Avatar
            src={lockedBy.imageThumbnail || undefined}
            sx={{
              width: 80,
              height: 80,
              bgcolor: lockedBy.imageThumbnail ? "transparent" : avatarColor,
              fontSize: "2rem",
              boxShadow: theme.shadows[3],
              borderRadius: "12px",
            }}
          >
            {!lockedBy.imageThumbnail &&
              lockedBy.userName?.charAt(0).toUpperCase()}
          </Avatar>

          <Box sx={{ textAlign: "center" }}>
            <Typography variant="body1" gutterBottom>
              This record is currently being edited by
            </Typography>
            <Typography variant="h6" color="primary">
              {lockedBy.userName}
            </Typography>
          </Box>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "center", mt: 1 }}
          >
            The record will become available when they finish editing or close
            the editor.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="contained" color="primary" fullWidth>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditLockDialog;
